import * as React from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";

type Props = {
  displayName: string;
  userRole?: string;
  unreadCount?: number;
  onProfile: () => void;
  hearts?: number;
  onHeartsPurchase?: () => void;
};

export default function DesktopSidebar({
  displayName,
  userRole,
  unreadCount,
  onProfile,
  hearts = 0,
  onHeartsPurchase,
}: Props) {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const isDashboard = pathname === "/dashboard" || pathname === "/";
  const isMessages = pathname.startsWith("/messages");

  const navItems = [
    {
      emoji: "🏠",
      label: "홈",
      active: isDashboard,
      onClick: () => navigate("/dashboard"),
      badge: undefined as number | undefined,
    },
    {
      emoji: "💌",
      label: "메시지",
      active: isMessages,
      onClick: () => navigate("/messages"),
      badge: unreadCount && unreadCount > 0 ? unreadCount : undefined,
    },
    {
      emoji: "🎴",
      label: "큐피트 카드",
      active: false,
      onClick: () => {
        if (isDashboard) {
          document.getElementById("cupid-cards-section")?.scrollIntoView({ behavior: "smooth" });
        } else {
          navigate("/dashboard");
        }
      },
      badge: undefined,
    },
    {
      emoji: "👤",
      label: "내 프로필",
      active: false,
      onClick: onProfile,
      badge: undefined,
    },
    {
      emoji: "🔔",
      label: "알림",
      active: pathname === "/notifications",
      onClick: () => navigate("/notifications"),
      badge: undefined,
    },
    {
      emoji: "⚙️",
      label: "설정",
      active: pathname === "/settings",
      onClick: () => navigate("/settings"),
      badge: undefined,
    },
    {
      emoji: "💬",
      label: "고객센터",
      active: pathname.startsWith("/support"),
      onClick: () => navigate("/support"),
      badge: undefined,
    },
    ...(userRole === "ADMIN"
      ? [
          {
            emoji: "🛡️",
            label: "관리자 페이지",
            active: false,
            onClick: () => { window.location.href = "/admin"; },
            badge: undefined as number | undefined,
          },
        ]
      : []),
  ];

  return (
    <Box
      sx={{
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        width: 240,
        flexShrink: 0,
        bgcolor: "#0D1F3C",
        position: "sticky",
        top: 0,
        height: "100vh",
        overflowY: "auto",
        "&::-webkit-scrollbar": { display: "none" },
        scrollbarWidth: "none",
        "&::after": {
          content: '""',
          position: "absolute",
          top: 0,
          right: 0,
          width: "2px",
          height: "100%",
          background:
            "linear-gradient(180deg, #C9A86C 0%, rgba(201,168,108,0.1) 60%, transparent 100%)",
          pointerEvents: "none",
        },
      }}
    >
      {/* 로고 */}
      <Box sx={{ px: 3, pt: 3.5, pb: 2 }}>
        <Typography
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontSize: "24px",
            color: "white",
            lineHeight: 1.2,
          }}
        >
          Silver
          <Typography
            component="span"
            sx={{
              fontFamily: '"Playfair Display", serif',
              fontSize: "24px",
              fontWeight: 700,
              color: "#C9A86C",
            }}
          >
            Connect
          </Typography>
        </Typography>
        <Typography
          sx={{
            fontSize: "11px",
            color: "rgba(255,255,255,0.35)",
            letterSpacing: "1.5px",
            mt: "4px",
          }}
        >
          ELEGANT CONNECTIONS
        </Typography>
      </Box>

      {/* 구분선 */}
      <Box
        sx={{ mx: 2.5, borderTop: "1px solid rgba(201,168,108,0.1)", mb: 1.5 }}
      />

      {/* 하트 잔액 위젯 */}
      <Box sx={{ px: 2, pb: 1.5 }}>
        <Box
          onClick={() => navigate("/wallet")}
          sx={{
            background: "rgba(201,168,108,0.12)",
            border: "1px solid rgba(201,168,108,0.3)",
            borderRadius: "12px",
            p: "14px 16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            transition: "all 0.15s ease",
            "&:hover": {
              background: "rgba(201,168,108,0.22)",
              borderColor: "rgba(201,168,108,0.5)",
            },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: "8px" }}>
            <Typography sx={{ fontSize: "16px", lineHeight: 1 }}>🤍</Typography>
            <Box>
              <Typography
                sx={{ fontSize: "11px", color: "rgba(255,255,255,0.45)", lineHeight: 1.2 }}
              >
                내 하트
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Playfair Display", serif',
                  fontSize: "20px",
                  fontWeight: 700,
                  color: "#C9A86C",
                  lineHeight: 1.2,
                }}
              >
                {hearts}
              </Typography>
            </Box>
          </Box>
          {onHeartsPurchase && (
            <Box
              component="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                onHeartsPurchase();
              }}
              sx={{
                bgcolor: "rgba(201,168,108,0.25)",
                border: "1px solid rgba(201,168,108,0.5)",
                borderRadius: "8px",
                px: "10px",
                py: "5px",
                cursor: "pointer",
                color: "#E8CFA0",
                fontSize: "11px",
                fontWeight: 600,
                fontFamily: "Pretendard, sans-serif",
                transition: "all 0.15s",
                "&:hover": {
                  bgcolor: "rgba(201,168,108,0.4)",
                },
              }}
            >
              충전
            </Box>
          )}
        </Box>
      </Box>

      {/* 네비게이션 */}
      <Box sx={{ px: 1.5, flex: 1 }}>
        {navItems.map((item, i) => (
          <Box
            key={i}
            component="button"
            onClick={item.onClick}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              width: "100%",
              pl: "12px",
              pr: "12px",
              py: "10px",
              bgcolor: item.active ? "rgba(201,168,108,0.12)" : "transparent",
              border: "none",
              borderLeft: item.active
                ? "3px solid #C9A86C"
                : "3px solid transparent",
              borderRadius: "0 10px 10px 0",
              cursor: "pointer",
              color: item.active ? "#E8CFA0" : "rgba(255,255,255,0.65)",
              fontSize: "14px",
              fontWeight: item.active ? 600 : 500,
              fontFamily: "Pretendard, sans-serif",
              textAlign: "left",
              transition: "all 0.15s",
              "&:hover": {
                bgcolor: "rgba(201,168,108,0.1)",
                color: "#E8CFA0",
              },
            }}
          >
            <span style={{ fontSize: 16 }}>{item.emoji}</span>
            <span>{item.label}</span>
            {item.badge ? (
              <Box
                sx={{
                  ml: "auto",
                  bgcolor: "#B87D8F",
                  color: "white",
                  borderRadius: "10px",
                  fontSize: "10px",
                  fontWeight: 700,
                  px: "6px",
                  py: "1px",
                  lineHeight: 1.4,
                }}
              >
                {item.badge}
              </Box>
            ) : null}
          </Box>
        ))}
      </Box>

      {/* 구분선 */}
      <Box
        sx={{ mx: 2.5, borderTop: "1px solid rgba(201,168,108,0.1)", mt: 1 }}
      />

      {/* 사용자 정보 (하단) */}
      <Box sx={{ px: 2.5, py: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              border: "1.5px solid #C9A86C",
              bgcolor: "rgba(201,168,108,0.15)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: '"Playfair Display", serif',
              fontSize: 16,
              color: "#C9A86C",
              flexShrink: 0,
              userSelect: "none",
            }}
          >
            {displayName?.[0] || "김"}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{
                fontSize: "13px",
                fontWeight: 700,
                color: "white",
                lineHeight: 1.3,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {displayName}
            </Typography>
            <Typography
              sx={{ fontSize: "11px", color: "rgba(255,255,255,0.45)" }}
            >
              승인된 회원
            </Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
